/**
 * The profile page's derivations, as plain functions.
 *
 * These live outside Profile.vue so they can be tested without mounting a
 * component — the state machine behind the reward track and the quest join
 * are where the bugs have actually been (rewards showing as claimed before
 * their data loaded; quests silently vanishing when the pool hadn't
 * arrived), and neither needed a DOM to get wrong.
 *
 * Everything here is pure: inputs in, values out, no store and no `this`.
 */

/** Rarity, weakest to strongest. */
export const RARITY_ORDER = ['common', 'rare', 'epic', 'legendary', 'mythical']

/** Where a rarity sits in that order; -1 for anything unrecognised. */
export const rarityRank = rarity => RARITY_ORDER.indexOf(rarity)

/**
 * Black or white, whichever is readable on `hex`.
 *
 * Derived rather than fixed to white because theme accents range from
 * near-black (Default) to pale cyan (Neon Nights) — white is unreadable on
 * the latter. Rec. 601 luma against a 0.6 midpoint.
 */
export function contrastOn(hex) {
    const value = String(hex || '').replace('#', '')
    if (value.length !== 6) return '#fff'
    const [r, g, b] = [0, 2, 4].map(i => parseInt(value.slice(i, i + 2), 16) / 255)
    if ([r, g, b].some(Number.isNaN)) return '#fff'
    return (0.299 * r + 0.587 * g + 0.114 * b) > 0.6 ? '#000' : '#fff'
}

/** Near-black. What a page frame is mixed towards — see `pageFrame`. */
const FRAME_TARGET = '#14161a'

/**
 * Blends two `#rrggbb` colours. `amount` is how much of `b` to take.
 *
 * Returns `a` unchanged if either side isn't a plain six-digit hex — themes
 * are authored by hand and a malformed colour should degrade to "no blend"
 * rather than to `#NaNNaNNaN`.
 */
export function mixHex(a, b, amount) {
    const parse = value => {
        const clean = String(value || '').replace('#', '')
        if (clean.length !== 6) return null
        const parts = [0, 2, 4].map(i => parseInt(clean.slice(i, i + 2), 16))
        return parts.some(Number.isNaN) ? null : parts
    }

    const from = parse(a)
    const to = parse(b)
    if (!from || !to) return a

    const t = Math.min(1, Math.max(0, amount))
    const channel = i => Math.round(from[i] + (to[i] - from[i]) * t)
    return '#' + [0, 1, 2].map(i => channel(i).toString(16).padStart(2, '0')).join('')
}

/**
 * The colour of the page *around* the profile panel.
 *
 * A flat `#eee` frame was fine when every theme was a pale gradient, but it
 * fights a themed panel — Arcade's near-black scene sat in a bright grey
 * surround that read as an unstyled page rather than a deliberate mount.
 *
 * The frame is the theme's own colour taken well down towards black: deeper
 * than the panel in every case, so it reads as a matte around a picture
 * rather than as a second competing surface. A scene theme offers `ground`
 * (the colour its scene settles into at the bottom), which continues the
 * scene outwards; anything else falls back to its secondary.
 */
export function pageFrame(theme) {
    if (!theme) return null
    const base = theme.ground || (theme.colors && theme.colors.secondary)
    if (!base) return null
    return mixHex(base, FRAME_TARGET, 0.55)
}

/**
 * XP into the current level over what the level costs — not lifetime XP,
 * which would show a bar that never resets.
 *
 * `xpLevels[i]` is the threshold for level `i + 1`, so the current level's
 * floor is `xpLevels[level - 1]` and its ceiling `xpLevels[level]`.
 *
 * @returns {{ floor: number, ceiling: number|null }} ceiling is null at max level.
 */
export function levelBounds(level, xpLevels) {
    const floor = level > 0 ? xpLevels[level - 1] : 0
    const ceiling = level < xpLevels.length ? xpLevels[level] : null
    return { floor, ceiling }
}

/** How far through the current level, clamped to 0–1. */
export function levelProgress(xp, level, xpLevels) {
    const { floor, ceiling } = levelBounds(level, xpLevels)
    if (ceiling === null) return 1
    const span = ceiling - floor
    if (span <= 0) return 0
    return Math.min(Math.max((xp - floor) / span, 0), 1)
}

/**
 * The state of one level on the reward track.
 *
 * Two of these were wrong in ways that told a free player something false,
 * so they're spelled out rather than inferred:
 *
 *  - A premium-gated level *above* the player's level still reads `locked`.
 *    The claimable endpoint only reports levels already reached, so nothing
 *    else marks the rest of the track as gated and a free player read their
 *    whole future track as rewards they'd receive.
 *  - Before the claim data arrives, a reached level is `unknown`, not
 *    `claimed`. An empty response and an unloaded one are indistinguishable
 *    otherwise, and the track drew checkmarks over rewards still owed.
 */
export function rewardState({
    level,
    userLevel,
    claimableLevels,
    lockedLevels,
    premiumOnly,
    isPremium,
    rewardsLoaded,
}) {
    if (claimableLevels.has(level)) return 'claimable'
    if (lockedLevels.has(level)) return 'locked'
    if (level > userLevel) return (premiumOnly && !isPremium) ? 'locked' : 'future'
    if (!rewardsLoaded) return 'unknown'
    return 'claimed'
}

/**
 * A user's assigned quests joined against the pool for the parts their
 * document doesn't store (description, target, reward).
 *
 * Returns an empty list when the pool hasn't loaded — the caller has to
 * distinguish that from "no quests", which is why `questsResolved` exists
 * separately. Quests whose definition has since left the pool are dropped
 * rather than rendered blank.
 */
export function joinQuests(userQuests, pool) {
    if (!pool || !pool.length) return []

    return (userQuests || []).map(userQuest => {
        const def = pool.find(q => q.id === userQuest.id)
        if (!def) return null

        const progress = Math.min(userQuest.progress || 0, def.target)
        return {
            id: userQuest.id,
            title: def.description,
            xpReward: def.xpReward,
            progress,
            target: def.target,
            completed: userQuest.completed === true,
            percent: `${Math.round((progress / def.target) * 100)}%`,
        }
    }).filter(Boolean)
}

/** Time until the daily quest reset, as `12h 30m`. */
export function questCountdown(lastQuest, resetMs, now) {
    const remaining = ((lastQuest || 0) + resetMs) - now
    if (remaining <= 0) return 'Soon'

    const hours = Math.floor(remaining / 3600000)
    const minutes = Math.floor((remaining % 3600000) / 60000)
    return `${hours}h ${String(minutes).padStart(2, '0')}m`
}

/**
 * A user's best achievements, rarest first, excluding the one they've
 * equipped as their title.
 *
 * `catalog` maps both achievement id and name to a definition, because
 * `updateAchievements` stores achievements by name while the catalog is keyed
 * by id — a user's list can legitimately hold either form, and the same
 * achievement can therefore resolve twice.
 */
export function topAchievements({ unlocked, catalog, equippedTitle, limit }) {
    const seen = new Set()
    const found = []

    for (const entry of unlocked || []) {
        const achievement = catalog[entry]
        if (!achievement || seen.has(achievement.id)) continue
        if (achievement.id === equippedTitle || achievement.name === equippedTitle) continue
        seen.add(achievement.id)
        found.push(achievement)
    }

    return found
        .sort((a, b) => rarityRank(b.rarity) - rarityRank(a.rarity))
        .slice(0, limit)
}

/** The games a user has played most, with their display names. */
export function mostPlayed(stats, gameNames, limit = 3) {
    return Object.entries(stats || {})
        .map(([id, entry]) => ({
            id,
            // Falls back to the raw key, so a game added since the catalog was
            // fetched still renders.
            name: (gameNames && gameNames[id]) || id,
            games: entry.games || 0,
            wins: entry.wins || 0,
        }))
        .sort((a, b) => b.games - a.games)
        .slice(0, limit)
}

/** Total wins across every game. */
export function totalWins(stats) {
    return Object.values(stats || {}).reduce((sum, entry) => sum + (entry.wins || 0), 0)
}

/** Total games across every game. */
export function totalGames(stats) {
    return Object.values(stats || {}).reduce((sum, entry) => sum + (entry.games || 0), 0)
}

/** Wins over games as a percentage, 0 when nothing has been played. */
export function winRate(wins, games) {
    if (!games) return 0
    return (wins / games) * 100
}

/**
 * The seasons a user can look at, newest first.
 *
 * A user's `stats` holds only the *current* season — rollover renames it into
 * `history.stats.<id>` and starts a fresh one — so the seasons on offer are
 * the current one plus whatever history they've accumulated. The current
 * season is always offered even with nothing played in it yet; "this season,
 * empty" is a real answer and an empty picker is not.
 *
 * `catalog` comes from GET /api/season, which ships every season from the
 * epoch to now. It's a handful of entries, so the client gets the lot up
 * front rather than asking per season.
 */
export function availableSeasons(dbInfo, catalog, currentId) {
    const info = dbInfo || {}
    const history = (info.history && info.history.stats) || {}

    const ids = new Set(Object.keys(history).map(Number).filter(Number.isFinite))
    if (Number.isFinite(currentId)) ids.add(currentId)

    const byId = (catalog || []).reduce((map, season) => {
        map[season.id] = season
        return map
    }, {})

    return [...ids]
        .sort((a, b) => b - a)
        // A history key with no catalog entry would render as a blank option.
        .map(id => byId[id])
        .filter(Boolean)
}

/** Distinct years across a list of seasons, newest first. */
export function seasonYears(seasons) {
    return [...new Set((seasons || []).map(season => season.year))].sort((a, b) => b - a)
}

/**
 * The per-game stats for one season.
 *
 * The current season reads from `stats`; anything earlier from the archive
 * rollover moved it to. An unknown season is an empty object rather than a
 * throw — a user can hold a stale selection when their history changes.
 */
export function statsForSeason(dbInfo, seasonId, currentId) {
    const info = dbInfo || {}
    if (seasonId === currentId) return info.stats || {}

    const history = (info.history && info.history.stats) || {}
    return history[seasonId] || {}
}

/** Every game the user has played, most games first, with derived numbers. */
export function gameBreakdown(stats, gameNames) {
    return Object.entries(stats || {}).map(([id, entry]) => {
        const games = entry.games || 0
        const wins = entry.wins || 0
        return {
            id,
            name: (gameNames && gameNames[id]) || id,
            games,
            wins,
            winRate: winRate(wins, games),
        }
    }).sort((a, b) => b.games - a.games)
}

/**
 * Games whose initials are not what anybody calls them.
 *
 * Keyed by game id, which is also where the right answer usually already
 * lives — Survey Says is `sus` in metadata.js and in every stats document,
 * so SUS is the name it already has everywhere except the one place that
 * derived its own.
 */
const ABBREVIATION_OVERRIDES = Object.freeze({
    sus: 'SUS',
})

/**
 * A game's name, short enough to sit inside a bubble.
 *
 * Anything that doesn't fit becomes its initials — "Cards Against Humanity"
 * is "CAH", which is what people call it anyway. Better than an ellipsis
 * ("Cards Ag…") and better than the raw stats key ("cah"), which means nothing
 * to a player.
 */
export function abbreviateGame(name, id) {
    const label = String(name || id || '')
    if (!label) return ''

    // Initials are a guess at what people call a game, and sometimes the
    // guess is wrong: "Survey Says" initialises to SS, which reads as an
    // abbreviation of nothing, while everyone — including its own stats key —
    // calls it SUS. Checked before the length rule, because an override is a
    // statement about the name rather than about how long it is.
    const override = ABBREVIATION_OVERRIDES[String(id || '').toLowerCase()]
    if (override) return override

    if (label.length <= 10) return label

    const initials = label
        .split(/[\s:_-]+/)
        .filter(Boolean)
        // Drop the joining words — "Cards Against Humanity" reads as CAH, not
        // CAH with the A standing for "Against".
        .filter((word, i) => i === 0 || !['and', 'of', 'the', 'or'].includes(word.toLowerCase()))
        .map(word => word[0])
        .join('')
        .toUpperCase()

    // A single long word has no initials worth showing ("S" for Scrabble
    // tells you nothing), so it comes back whole and the caller decides
    // whether there's room for it.
    return initials.length > 1 ? initials : label
}

/**
 * Text that runs around the inside edge of a bubble, as an SVG path plus the
 * string to lay along it.
 *
 * The band is a fixed thickness on every bubble — same font size, same inset
 * from the edge — so the circles read as a set rather than as the same design
 * at six different scales.
 *
 * Returns null when the circle is too small to carry any text at all; the
 * caller draws just the number in that case.
 */
export function ringLayout(bubble, options) {
    const opts = options || {}
    const fontSize = opts.fontSize === undefined ? 9 : opts.fontSize
    const inset = opts.inset === undefined ? 13 : opts.inset
    const separator = opts.separator === undefined ? ' • ' : opts.separator
    // How many times a label has to go round before it's worth reading. One
    // pass that only just closes the loop reads as a dense band of letters
    // rather than as a name; twice round reads as a pattern.
    const minRepeats = opts.minRepeats === undefined ? 2 : opts.minRepeats

    const radius = bubble.r
    if (!radius) return null

    const ringRadius = radius - inset
    // Text needs a circle meaningfully larger than the text is tall, or the
    // letters splay outwards and stop resembling a line. Expressed against
    // the font size so it stays true if the band is ever restyled.
    if (ringRadius < fontSize * 1.5) return null

    const circumference = 2 * Math.PI * ringRadius
    // Estimated rather than measured: laying the text out to measure it costs
    // a layout pass per bubble per resize. `textLength` below absorbs the
    // error by adjusting letter spacing, so an approximation is enough.
    const widthOf = text => text.length * fontSize * 0.72
    /** How many whole passes of `text` the circle can carry. */
    const passes = text => Math.floor(circumference / widthOf(text + separator))

    // Longest form first, each a fallback for the one before it. The stats
    // key is the last resort because it's always short and already doubles as
    // a label elsewhere — the Overview tab's circles show it raw.
    const candidates = [
        String(bubble.name || '').toUpperCase(),
        String(abbreviateGame(bubble.name, bubble.id)).toUpperCase(),
        String(bubble.id || '').toUpperCase(),
    ]

    // One rule, applied to every candidate rather than only to the full name:
    // take the longest form that goes round at least twice.
    //
    // Letting a single pass through was the mistake. `textLength` stretches
    // whatever it's given across the whole circumference, so one pass of
    // "CONNECT 4" isn't a name on a ring — it's nine letters spaced half a
    // circle apart. Below two passes there's no readable ring to draw.
    const label = candidates.find(candidate => candidate && passes(candidate) >= minRepeats)
    if (!label) return null

    return {
        text: (label + separator).repeat(passes(label)),
        // Starts at the bottom right, which is where the eye lands on a
        // circle carrying a number in the middle.
        path: circlePath(radius, radius, ringRadius, 45),
        // Handed to textLength so the repeated text closes the loop exactly
        // rather than leaving a gap or overlapping at the seam.
        length: circumference,
        fontSize,
    }
}

/** A full circle as an SVG path, starting at `startAngle` and running clockwise. */
function circlePath(cx, cy, r, startAngle) {
    const radians = (startAngle * Math.PI) / 180
    const round = n => Math.round(n * 100) / 100
    const x0 = round(cx + r * Math.cos(radians))
    const y0 = round(cy + r * Math.sin(radians))
    const x1 = round(cx - r * Math.cos(radians))
    const y1 = round(cy - r * Math.sin(radians))
    const rr = round(r)

    // Two half-circle arcs, because a single arc can't describe a full circle
    // — its start and end points would coincide and it would draw nothing.
    return `M ${x0} ${y0} A ${rr} ${rr} 0 1 1 ${x1} ${y1} A ${rr} ${rr} 0 1 1 ${x0} ${y0}`
}

/**
 * A small, stable tilt for a bubble, in degrees.
 *
 * Derived from the id rather than Math.random so a given game always leans
 * the same way — a random angle would reshuffle on every re-render and every
 * resize, which reads as a glitch rather than as charm.
 */
export function tiltFor(id, maxDegrees = 30) {
    const key = String(id || '')

    // FNV-1a with a mixing finalizer, rather than the obvious `hash * 31 + c`.
    //
    // The simple version doesn't avalanche: keys that differ only in the last
    // character ("che:1", "che:2", …) come out within 0.01° of each other, so
    // every tap on a bubble nudged it by an identical amount in an identical
    // direction and it marched steadily in one direction. Sequential seeds
    // are exactly the case this is used for, so the hash has to scatter them.
    let hash = 2166136261
    for (let i = 0; i < key.length; i++) {
        hash ^= key.charCodeAt(i)
        hash = Math.imul(hash, 16777619)
    }
    hash ^= hash >>> 15
    hash = Math.imul(hash, 2246822507)
    hash ^= hash >>> 13
    hash >>>= 0

    // 0..1 → -1..1 → scaled.
    return ((hash / 0xFFFFFFFF) * 2 - 1) * maxDegrees
}

/**
 * A new angle for a bubble that was just tapped: the current one, nudged.
 *
 * Deliberately a *change* rather than a fresh angle — the bubble should look
 * like it got knocked askew, not like it teleported to a new pose.
 *
 * A nudge that would take it past the limit is applied the other way instead,
 * so repeated taps keep rocking it back and forth rather than jamming it
 * against the bound and appearing to stop responding.
 */
export function nudgeTilt(current, seed, options) {
    const opts = options || {}
    const max = opts.max === undefined ? 30 : opts.max
    const step = opts.step === undefined ? 13 : opts.step
    const minimum = opts.minimum === undefined ? 4 : opts.minimum

    let delta = tiltFor(seed, step)
    // A nudge too small to see is the same as no response at all.
    if (Math.abs(delta) < minimum) delta = delta >= 0 ? minimum : -minimum

    const next = current + delta
    const reflected = (next > max || next < -max) ? current - delta : next

    return Math.max(-max, Math.min(max, reflected))
}

/**
 * Positions the game bubbles.
 *
 * Radius goes as the *square root* of wins, so it's the circle's **area**
 * that's proportional — scaling the radius directly is the classic way to lie
 * with bubbles, where a game won 4× as often would look 16× as big. Wins
 * rather than games played, so the size and the number inside the bubble
 * (also wins) are never telling two different stories.
 *
 * Placement is a greedy spiral out from the centre: each bubble, largest
 * first, takes the first spot on the spiral where it clears everything already
 * down. That puts the heavyweights in the middle and scatters the rest around
 * them, which is the arrangement that reads as a cluster rather than a chart.
 *
 * Deterministic — no Math.random anywhere — so the same stats always produce
 * the same picture and this can be tested without a DOM.
 *
 * @returns {{ bubbles: object[], height: number }} height is what the
 *   container must be for the cluster to fit.
 */
export function bubbleLayout(games, options) {
    const opts = options || {}
    const width = opts.width || 600
    const gap = opts.gap === undefined ? 8 : opts.gap
    const minHeight = opts.minHeight || 200

    // Games played, and the same quantity drives the size and the number in
    // the bubble — the picture and the label agree, which is the property
    // worth keeping from when both were wins.
    //
    // Plays rather than wins, because a panel about what you play should
    // answer that. Sizing by wins also hid people from their own stats: a
    // player with fifty games and no wins had *no bubbles at all* and was
    // told "No games played yet", which was simply untrue.
    const rows = (games || []).filter(game => game.games > 0)
    if (!rows.length) return { bubbles: [], height: minHeight }

    // `=== undefined` rather than `||`: a caller asking for a floor of 0 wants
    // pure area scaling, and `0 || 26` would silently hand back 26.
    const maxRadius = opts.maxRadius === undefined ? 72 : opts.maxRadius
    const minRadius = opts.minRadius === undefined ? 26 : opts.minRadius

    // The biggest bubble can never be wider than the strip it sits in, which
    // is what keeps this working on a phone without a separate mobile case.
    const cap = Math.max(20, Math.min(maxRadius, width / 2 - gap))
    // A floor above zero deliberately breaks strict area proportionality at
    // the bottom end: a game played once would otherwise be a 2px dot with no
    // room for the count, which is the number the bubble exists to show.
    const floor = Math.min(minRadius, cap)
    const most = Math.max.apply(null, rows.map(game => game.games))

    const sized = rows.map(game => ({
        ...game,
        r: floor + (cap - floor) * Math.sqrt(game.games / most),
    })).sort((a, b) => b.r - a.r)

    // Circles can't tile, so the box has to be meaningfully larger than the
    // area of what goes in it. Start generous and grow until everything fits
    // rather than guessing one number that has to work for every stat spread.
    const needed = sized.reduce((sum, b) => sum + Math.PI * (b.r + gap) * (b.r + gap), 0)

    for (let attempt = 0; attempt < 12; attempt++) {
        const box = Math.max(minHeight, (needed * (1.45 + attempt * 0.3)) / width)
        const placed = packBubbles(sized, width, box, gap)
        if (!placed) continue

        // The search box is deliberately roomier than the cluster needs, so
        // it always has somewhere to put the next circle. Trimming back to
        // what was actually used is what stops the panel from carrying a
        // band of empty space above and below.
        const top = Math.min.apply(null, placed.map(b => b.y - b.r))
        const bottom = Math.max.apply(null, placed.map(b => b.y + b.r))
        const left = Math.min.apply(null, placed.map(b => b.x - b.r))
        const right = Math.max.apply(null, placed.map(b => b.x + b.r))
        const recentre = (width - (right - left)) / 2 - left

        return {
            bubbles: placed.map((bubble, i) => ({
                ...bubble,
                x: bubble.x + recentre,
                y: bubble.y - top,
                // Small bubbles travel further as the section scrolls past;
                // that difference is what reads as depth. The phase keeps
                // neighbours from moving in lockstep.
                drift: 6 + Math.round((1 - (bubble.r - floor) / Math.max(cap - floor, 1)) * 26),
                phase: (i % 4) / 4,
            })),
            height: Math.ceil(bottom - top),
        }
    }

    // Unreachable for any real stat spread — twelve attempts is a box roughly
    // five times the area of its contents. Returning an unplaced list beats
    // throwing on someone's profile page.
    return { bubbles: [], height: minHeight }
}

/**
 * Greedy spiral packing. Returns null if anything couldn't be placed, which
 * is the caller's signal to try a taller box.
 */
function packBubbles(sized, width, height, gap) {
    const placed = []
    const cx = width / 2
    const cy = height / 2

    for (const bubble of sized) {
        let spot = null

        // Archimedean spiral, squashed vertically because these strips are
        // much wider than they are tall — a round spiral would run out of
        // vertical room and give up while the sides were still empty.
        for (let step = 0; step < 3000 && !spot; step++) {
            const angle = step * 0.25
            const distance = step * 0.35
            const x = cx + Math.cos(angle) * distance
            const y = cy + Math.sin(angle) * distance * (height / width)

            if (x - bubble.r < 0 || x + bubble.r > width) continue
            if (y - bubble.r < 0 || y + bubble.r > height) continue

            const clears = placed.every(other => {
                const dx = other.x - x
                const dy = other.y - y
                return Math.sqrt(dx * dx + dy * dy) >= other.r + bubble.r + gap
            })

            if (clears) spot = { x, y }
        }

        if (!spot) return null
        placed.push({ ...bubble, x: spot.x, y: spot.y })
    }

    return placed
}

/**
 * Which catalog achievements this user has unlocked, as a Set of ids.
 *
 * A user's stored list can hold either an id or a name (updateAchievements
 * writes names; the catalog is keyed by id), so entries are resolved through
 * the catalog rather than compared directly — otherwise the same achievement
 * counts twice, or not at all.
 */
export function unlockedAchievementIds(unlocked, catalog) {
    const ids = new Set()
    for (const entry of unlocked || []) {
        const achievement = catalog[entry]
        if (achievement) ids.add(achievement.id)
    }
    return ids
}

/**
 * The whole catalog grouped by category, with each entry marked unlocked or
 * not.
 *
 * Locked achievements are included on purpose: the descriptions are already
 * public (GET /api/achievements), and a page that only lists what you have
 * gives you no reason to play. `validate` is the part that stays server-side.
 *
 * Categories are ordered by how far through them the user is, so the ones
 * they're actually working on rise to the top and the untouched games sink.
 */
export function achievementSections(catalog, unlockedIds) {
    const byCategory = new Map()

    for (const achievement of catalog || []) {
        const category = achievement.category || 'General'
        if (!byCategory.has(category)) byCategory.set(category, [])
        byCategory.get(category).push({
            ...achievement,
            unlocked: unlockedIds.has(achievement.id),
        })
    }

    return [...byCategory.entries()].map(([category, achievements]) => {
        const unlockedCount = achievements.filter(a => a.unlocked).length
        return {
            category,
            // Rarest first within a section, and unlocked before locked, so
            // what the user has earned leads and the hardest thing left is
            // the first one they see below it.
            achievements: achievements.slice().sort((a, b) => {
                if (a.unlocked !== b.unlocked) return a.unlocked ? -1 : 1
                return rarityRank(b.rarity) - rarityRank(a.rarity)
            }),
            unlockedCount,
            total: achievements.length,
            percent: achievements.length ? unlockedCount / achievements.length : 0,
        }
    }).sort((a, b) => b.percent - a.percent || a.category.localeCompare(b.category))
}

/** Headline counts for the achievements panel, including a per-rarity tally. */
export function achievementSummary(catalog, unlockedIds) {
    const byRarity = RARITY_ORDER.map(rarity => {
        const inTier = (catalog || []).filter(a => a.rarity === rarity)
        return {
            rarity,
            unlocked: inTier.filter(a => unlockedIds.has(a.id)).length,
            total: inTier.length,
        }
    // A tier nothing exists in yet would render as an empty 0/0 chip.
    }).filter(tier => tier.total > 0)

    const total = (catalog || []).length
    const unlocked = (catalog || []).filter(a => unlockedIds.has(a.id)).length

    return { unlocked, total, percent: total ? unlocked / total : 0, byRarity }
}

/**
 * Calls `fn` at most once per animation frame's worth of events, and always
 * once more after the burst ends.
 *
 * Used for the resize handler: fitTags() forces a layout pass per candidate
 * pill, and dragging a window edge fires resize dozens of times a second.
 */
export function debounce(fn, wait = 120) {
    let timer = null
    const debounced = (...args) => {
        if (timer) clearTimeout(timer)
        timer = setTimeout(() => { timer = null; fn(...args) }, wait)
    }
    debounced.cancel = () => { if (timer) clearTimeout(timer); timer = null }
    return debounced
}
